import React, { useId, useState } from 'react';
import { Link } from 'expo-router';
import Icon from '@/components/Icon';
import { LEARN_LESSONS, LEARN_MODULES } from '@/constants/learnCurriculum';
import { useLearnProgress } from './LearnProgress';

export default function LearnLessonLibrary() {
  const id = useId();
  const { completed } = useLearnProgress();
  const [topic, setTopic] = useState('all');
  const [query, setQuery] = useState('');
  const terms = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  const groups = LEARN_MODULES
    .filter(module => topic === 'all' || module.id === topic)
    .map(module => ({
      ...module,
      lessons: module.lessons.filter(lesson => {
        const text = `${lesson.label} ${lesson.slug.replace(/-/g, ' ')} ${module.title}`.toLowerCase();
        return terms.every(term => text.includes(term));
      }),
    }))
    .filter(module => module.lessons.length);
  const count = groups.reduce((sum, module) => sum + module.lessons.length, 0);
  const filtered = topic !== 'all' || terms.length > 0;
  function clear() { setTopic('all'); setQuery(''); }

  return <section id="learn-lessons" className="learn-catalog" aria-labelledby={`${id}-title`}>
    <div className="learn-section-heading">
      <div><h2 id={`${id}-title`}>All lessons.</h2><p className="learn-catalog-intro">Start anywhere. Choose something you want to learn.</p></div>
      <a href="#learn-examples" className="learn-text-button">Watch an example <span aria-hidden="true">↓</span></a>
    </div>
    <div className="learn-catalog-toolbar">
      <div className="learn-catalog-search"><span aria-hidden="true"><Icon name="magnifying-glass" size={16} color="var(--learn-muted)" /></span><label htmlFor={`${id}-search`} className="learn-demo-sr">Search all lessons</label><input id={`${id}-search`} type="search" placeholder="Search lessons" value={query} onChange={event => setQuery(event.target.value)} /></div>
      <p className="learn-fine" role="status">{count} {filtered ? `of ${LEARN_LESSONS.length} lessons` : 'lessons'}{filtered && <button className="learn-text-button" onClick={clear}>Clear filters</button>}</p>
    </div>
    <div className="learn-catalog-topics" role="group" aria-label="Filter lessons by topic">
      {[{ id: 'all', title: 'All lessons' }, ...LEARN_MODULES].map(module => <button key={module.id} aria-pressed={topic === module.id} onClick={() => setTopic(module.id)}>{module.title}</button>)}
    </div>
    <div className="learn-catalog-results">
      {groups.map(module => <section key={module.id} className="learn-catalog-group" aria-labelledby={`${id}-${module.id}`}>
        <h3 id={`${id}-${module.id}`}>{module.title}<span>{module.lessons.length} {module.lessons.length === 1 ? 'lesson' : 'lessons'}</span></h3>
        <div className="learn-catalog-grid">{module.lessons.map(lesson => <Link key={lesson.slug} href={`/docs/learn/${lesson.slug}`} className="learn-lesson-card">
          <div className="learn-lesson-card-icon" aria-hidden="true"><Icon name={lesson.icon || module.icon} size={34} color="var(--learn-ink)" /></div>
          <div className="learn-lesson-card-bottom"><div><h4>{lesson.label}</h4><p>{lesson.minutes} min reading{completed[lesson.slug] && <span className="learn-lesson-card-complete"> · ✓ Complete</span>}</p></div><span className="learn-lesson-card-open" aria-hidden="true"><Icon name="arrow-up-right" size={12} color="var(--learn-muted)" /></span></div>
        </Link>)}</div>
      </section>)}
      {!count && <div className="learn-catalog-empty"><h3>No lessons found.</h3><p>Try a different topic or a shorter search, such as “writing” or “video”.</p><button className="learn-button learn-button-secondary" onClick={clear}>Show all lessons</button></div>}
    </div>
  </section>;
}
